import Button from '@/shared/Button';
import Form from '@/shared/Form';
import InputFieldBig from '@/shared/crisp/InputFieldBig';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Map from '../Map';
import Div from '@/shared/Div';
import Paragraph from '@/shared/Paragraph';
import { inputOnlyNumbers } from '@/utils/formValidation';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { addProfileInfo, updatePrimaryUsername, updateProfileInfo } from '@/actions/ecom/profileV2';
import useModalToggle from '@/hooks/useModalToggle';
import Modal from '@/shared/crisp/Modal';
import {
  D365_USER_ID,
  FORMTYPES,
  HEADING_LABELS,
  SHOW_PROFILE_TOAST,
} from '@/constants/profileManagement';
import useToast from '@/hooks/useToast';
import ProfileOTP from './ProfileOTP';
import Cookies from '@/utils/cookie';

function ProfileUpdateForm({ formFieldList, formType, headingLabel, profileData, contactId }: any) {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({});
  const { open, openModal, closeModal } = useModalToggle(false);
  const [formData, setFormData] = useState({});

  const { contacts, firstName, lastName } = profileData || {};

  const primaryMobileNumber = useMemo(() => {
    return contacts.find((contactInfo: any) => contactInfo.isPrimary && contactInfo.phone !== null)
      ?.phone;
  }, [contacts]);
  const { triggerDefault } = useToast();

  const addProfile = (...args: any) => addProfileInfo.apply(null, args);
  const {
    isLoading: addProfileLoadiing,
    promisifyTrigger: addProfileTrigger,
    isError,
    error,
  } = useTriggerRequest({ fetch: addProfile });

  const updateProfile = (...args: any) => updateProfileInfo.apply(null, args);
  const {
    promisifyTrigger: updateProfileTrigger,
    isLoading: updateProfileLoading,
    isError: isUpdateProfileError,
    error: updateProfileError,
  } = useTriggerRequest({ fetch: updateProfile });

  const primaryUsername = (...args: any) => updatePrimaryUsername.apply(null, args);
  const {
    promisifyTrigger: primaryUsernameTrigger,
    isLoading: usernameLoading,
    isError: isUsernameError,
    error: usernameError,
  } = useTriggerRequest({
    fetch: primaryUsername,
  });

  useEffect(() => {
    if (FORMTYPES.EDIT_PRIMARY_USER) {
      const initialFormData = {
        firstName,
        lastName,
      };
      setFormData(initialFormData);
      reset(initialFormData);
    }
  }, [firstName, lastName, reset]);

  useEffect(() => {
    if (isError && error?.error) {
      triggerDefault({
        message: error?.error,
        durationInms: 5000,
      });
    } else if (isUpdateProfileError && updateProfileError?.error) {
      triggerDefault({
        message: updateProfileError?.error,
        durationInms: 5000,
      });
    } else if (isUsernameError && usernameError?.error) {
      triggerDefault({
        message: usernameError?.error,
        durationInms: 5000,
      });
    }
  }, [
    error?.error,
    isError,
    isUpdateProfileError,
    isUsernameError,
    updateProfileError?.error,
    usernameError?.error,
  ]);

  const renderFormField = useCallback(
    (item: any, index: number) => (
      <Controller
        key={item?.name}
        name={item?.name}
        control={control}
        rules={item?.rules}
        render={({ field }) => (
          <InputFieldBig
            id={item?.name}
            label={item?.label}
            {...field}
            maxLength={item?.rules?.maxLength}
            onKeyPress={item?.name === 'mobileNumber' ? inputOnlyNumbers : undefined}
          />
        )}
      />
    ),
    [control],
  );

  const isProfileUpdate =
    (formType === FORMTYPES.EDIT_SECONDARY_USER || formType === FORMTYPES.EDIT_EMAIL) &&
    headingLabel !== HEADING_LABELS.ADD_SECONDARY_USER &&
    headingLabel !== HEADING_LABELS.ADD_EMAIL;

  const formSubmitHandler = async (formData: any) => {
    setFormData(formData);
    if (isProfileUpdate) {
      await updateProfileTrigger({
        contactId,
        contact: formData?.mobileNumber || formData.email,
        name: formType === FORMTYPES.EDIT_EMAIL ? `${firstName} ${lastName}` : formData?.name,
      });
      openModal();
    } else if (formType === FORMTYPES.EDIT_SECONDARY_USER || formType === FORMTYPES.EDIT_EMAIL) {
      await addProfileTrigger({
        d365UserId: Cookies.get(D365_USER_ID) || '',
        contact: formData?.mobileNumber || formData?.email,
        name: formType === FORMTYPES.EDIT_EMAIL ? `${firstName} ${lastName}` : formData?.name,
      });
      openModal();
    } else if (formType === FORMTYPES.EDIT_PRIMARY_USER) {
      await primaryUsernameTrigger({
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        d365UserId: Cookies.get(D365_USER_ID) || '',
      });
      sessionStorage.setItem(SHOW_PROFILE_TOAST, 'true');

      window.location.reload();
    }
  };

  return (
    <>
      <Div className="flex flex-col gap-s6">
        <Paragraph className="text-h-3">{headingLabel}</Paragraph>
        <Form className="flex flex-wrap flex-col gap-s5" onSubmit={handleSubmit(formSubmitHandler)}>
          <Map list={formFieldList} render={renderFormField} />
          <Button
            className="mt-s6"
            type="submit"
            isLoading={updateProfileLoading || addProfileLoadiing || usernameLoading}
          >
            Save
          </Button>
        </Form>
      </Div>

      {open ? (
        <Modal
          id="non-available-products"
          open={open}
          onClose={closeModal}
          classNames=" w-full bottom-0 fixed md:bottom-auto md:relative md:w-[416px] shadow-xbold"
        >
          <ProfileOTP
            mobileNumber={primaryMobileNumber}
            contactId={contactId}
            isProfileUpdate={isProfileUpdate}
            formData={formData}
            formType={formType}
            name={{ firstName, lastName }}
          />
        </Modal>
      ) : null}
    </>
  );
}

export default ProfileUpdateForm;
